/**
 * Component for RowItem
 *
 * @module components/RowItem
 */

import values from 'lodash/values';
import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router';

import ErrorBoundary from '../../../components/ErrorBoundary/ErrorBoundary';
import { KeyCodes } from '../../../constants/App';
import QATags from '../../../constants/QATags';
import { SourceCardConstants } from '../../../constants/SourceCard';
import { AppContext } from '../../../lib/Context';
import { handlerNoPropagate } from '../../../lib/EventUtils';
import { createBEM } from '../../../lib/utils/css';
import SourceCard from '../SourceCard/SourceCard';

import RowItemCenterColumn from './CenterColumn/RowItemCenterColumn';
import RowItemDescription from './Description/RowItemDescription';
import RowItemLeftColumn from './LeftColumn/RowItemLeftColumn';
import RowItemRightColumn from './RightColumn/RowItemRightColumn';

import './RowItem.scss';

export class RowItem extends React.PureComponent {
  static propTypes = {
    mainText: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    mainTextModifier: PropTypes.node,
    mainTextOnclick: PropTypes.func,
    mainTextUrl: PropTypes.string,

    secondText: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    secondTextModifier: PropTypes.any,
    secondTextOnclick: PropTypes.func,
    secondTextUrl: PropTypes.string,

    thirdText: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    thirdTextModifier: PropTypes.string,
    thirdTextOnclick: PropTypes.func,
    thirdTextUrl: PropTypes.string,

    qaEllipsis: PropTypes.oneOf(values(QATags)),
    qaImg: PropTypes.oneOf(values(QATags)),
    qaLeftColButton: PropTypes.oneOf(values(QATags)),
    qaMainText: PropTypes.oneOf(values(QATags)),
    qaParagraphText: PropTypes.oneOf(values(QATags)),
    qaRightColChild: PropTypes.oneOf(values(QATags)),
    qaRow: PropTypes.oneOf(values(QATags)),
    qaSecondText: PropTypes.oneOf(values(QATags)),
    qaThirdText: PropTypes.oneOf(values(QATags)),

    canShuffle: PropTypes.bool,
    date: PropTypes.instanceOf(Date),
    description: PropTypes.string,
    descriptionOnclick: PropTypes.func,
    descriptionUrl: PropTypes.string,
    dominantColor: PropTypes.string,
    enableSourceCardMenu: PropTypes.bool,
    hasBorderBottom: PropTypes.bool,
    hasBorderTopBottom: PropTypes.bool,
    hasCuratedModes: PropTypes.bool,
    hasHoverBottom: PropTypes.bool,
    hasHoverTop: PropTypes.bool,
    hasPlusIcon: PropTypes.bool,
    hasTakeoverModes: PropTypes.bool,
    hideLeftCol: PropTypes.bool,
    imgUrl: PropTypes.string,
    isBadgeFilled: PropTypes.bool,
    isClean: PropTypes.bool,
    isClickableRow: PropTypes.bool,
    isCollected: PropTypes.bool,
    isCustomBadge: PropTypes.string,
    isDisabled: PropTypes.bool,
    isExplicit: PropTypes.bool,
    isFadeHighlight: PropTypes.bool,
    isFullRowLink: PropTypes.bool,
    isImgCircle: PropTypes.bool,
    isLargeText: PropTypes.bool,
    isPaused: PropTypes.bool,
    isPlaceholder: PropTypes.bool,
    isPlaying: PropTypes.bool,
    isRadioOnly: PropTypes.bool,
    isRightColPersistent: PropTypes.bool,
    isRowSmall: PropTypes.bool,
    isShuffleDisabled: PropTypes.bool,
    isStation: PropTypes.bool,
    isUnavailable: PropTypes.bool,
    isWidthFull: PropTypes.bool,
    isWidthHalf: PropTypes.bool,
    noBorder: PropTypes.bool,
    noHover: PropTypes.bool,
    onlyShowRightColPlay: PropTypes.bool,
    onRightColChildClick: PropTypes.func,
    pauseButtonOnclick: PropTypes.func,
    playButtonOnclick: PropTypes.func,
    rightColChild: PropTypes.node,
    rightColChildHover: PropTypes.node,
    rowNumber: PropTypes.number,
    rowOnclick: PropTypes.func,
    rowUrl: PropTypes.string,
    shouldHideBadges: PropTypes.bool,
    shouldLinkImg: PropTypes.bool,
    shuffleOnclick: PropTypes.func,
    shuffleOnclickIfDisabled: PropTypes.func,
    sourceCardMenuItems: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    useDarkTheme: PropTypes.bool,
    useLightTheme: PropTypes.bool,
  };

  static defaultProps = {
    enableSourceCardMenu: true,
  };

  state = {
    isRowHovered: false,
    isSourceCardOpen: false,
  };

  static contextType = AppContext;

  isInteractive = () => {
    return (!this.props.noHover && this.state.isRowHovered) || this.state.isSourceCardOpen;
  };

  handleMouseEnter = () => {
    this.setState({ isRowHovered: true });
  };

  handleMouseLeave = () => {
    this.setState({ isRowHovered: false });
  };

  onKeyDown = e => {
    const key = e.which || e.keyCode;

    if (
      (this.state.isRowHovered && key === KeyCodes.TAB) ||
      (this.state.isRowHovered && key === KeyCodes.UP_ARROW_KEY) ||
      (this.state.isRowHovered && key === KeyCodes.DOWN_ARROW_KEY)
    ) {
      this.setState({ isRowHovered: false });
    }
  };

  onFocus = () => {
    if (!this.state.isRowHovered) {
      this.setState({ isRowHovered: true });
    }
  };

  onRowClick = e => {
    this.context.integration.action.clicked(
      this.context.integration.getActionData({
        destinationUrl: e.currentTarget.pathname,
      }),
    );
  };

  sourceCardToggleCallback = isSourceCardOpen => {
    this.setState({
      isSourceCardOpen,
    });
    if (!isSourceCardOpen) {
      this.setState({
        isRowHovered: false,
      });
    }
  };

  renderSourceCardWrappedContent = () => {
    // Necessary to have SourceCard triggered on right-clicking the RowItem
    return (
      <SourceCard
        type={[SourceCardConstants.CONTEXTMENU]}
        sourceCardMenuItems={this.props.sourceCardMenuItems}
        onToggleActive={this.sourceCardToggleCallback}
        qaTag={this.props.qaRow}
      >
        {this.renderRowItemContent()}
      </SourceCard>
    );
  };

  renderPlaceholder = classes => {
    const shouldShowDescription = !!this.props.description && !this.props.isRowSmall;
    const descriptionPlaceholder = <div className='RowItem__description__placeholder'></div>;
    return (
      <div className={classes}>
        <div className='RowItem__content'>
          <div className='RowItem__columns'>
            <div className='RowItem__leftCol'>
              <div className='RowItem__leftCol__placeholder' />
            </div>
            <div className='RowItem__centerCol'>
              <div className='RowItem__centerCol__mainText' />
              <div className='RowItem__centerCol__secondText' />
            </div>
            <div className='RowItem__rightCol'>
              {this.props.isRowSmall && <div className='RowItem__rightCol__placeholder' />}
            </div>
          </div>
          <div className='RowItem__description'>
            {shouldShowDescription && descriptionPlaceholder}
            {shouldShowDescription && descriptionPlaceholder}
          </div>
        </div>
      </div>
    );
  };

  renderLeftCol = () => {
    if (this.props.hideLeftCol) return null;

    return (
      <RowItemLeftColumn
        imgUrl={this.props.imgUrl}
        isLarge={this.props.isLargeText}
        description={this.props.description}
        isImgCircle={this.props.isImgCircle}
        shouldLinkImg={this.props.shouldLinkImg}
        mainText={this.props.mainText}
        mainTextUrl={this.props.mainTextUrl}
        mainTextOnclick={this.props.mainTextOnclick}
        isStation={this.props.isStation}
        dominantColor={this.props.dominantColor}
        qaImg={this.props.qaImg}
        isInteractive={this.isInteractive()}
        isFullRowLink={this.props.isFullRowLink}
        rowNumber={this.props.rowNumber}
        isDisabled={this.props.isDisabled}
        playButtonOnclick={this.props.playButtonOnclick}
        pauseButtonOnclick={this.props.pauseButtonOnclick}
        onlyShowRightColPlay={this.props.onlyShowRightColPlay}
        qaLeftColButton={this.props.qaLeftColButton}
        date={this.props.date}
        isPlaying={this.props.isPlaying}
        isRowSmall={this.props.isRowSmall}
        useDarkTheme={this.props.useDarkTheme}
        isCollected={this.props.isCollected}
        isPaused={this.props.isPaused}
        hasPlusIcon={this.props.hasPlusIcon}
        useLightTheme={this.props.useLightTheme}
      />
    );
  };

  renderCenterCol = () => {
    return (
      <RowItemCenterColumn
        mainText={this.props.mainText}
        mainTextUrl={!this.props.rowUrl ? this.props.mainTextUrl : null}
        mainTextOnclick={this.props.mainTextOnclick}
        qaMainText={this.props.qaMainText}
        mainTextModifier={this.props.mainTextModifier}
        isUnavailable={this.props.isUnavailable}
        secondText={this.props.secondText}
        secondTextUrl={this.props.secondTextUrl}
        secondTextOnclick={this.props.secondTextOnclick}
        qaSecondText={this.props.qaSecondText}
        secondTextModifier={this.props.secondTextModifier}
        thirdText={this.props.thirdText}
        thirdTextUrl={this.props.thirdTextUrl}
        thirdTextOnclick={this.props.thirdTextOnclick}
        qaThirdText={this.props.qaThirdText}
        thirdTextModifier={this.props.thirdTextModifier}
        rightColChild={this.props.rightColChild}
        qaRightColChild={this.props.qaRightColChild}
        isRowSmall={this.props.isRowSmall}
        isRightColPersistent={this.props.isRightColPersistent}
        isPlaceholder={this.props.isPlaceholder}
        isLargeText={this.props.isLargeText}
        useLightTheme={this.props.useLightTheme}
        useDarkTheme={this.props.useDarkTheme}
        isFullRowLink={this.props.isFullRowLink}
        isRowHovered={this.state.isRowHovered}
        hasTakeoverModes={this.props.hasTakeoverModes}
        hasCuratedModes={this.props.hasCuratedModes}
      />
    );
  };

  renderRightCol = () => {
    return (
      <RowItemRightColumn
        isInteractive={this.isInteractive()}
        isFullRowLink={this.props.isFullRowLink}
        isRowExtraLarge={!!this.props.description}
        canShuffle={this.props.canShuffle}
        // Themes
        useDarkTheme={this.props.useDarkTheme}
        useLightTheme={this.props.useLightTheme}
        // RowItemStandardBadges
        shouldHideBadges={this.props.shouldHideBadges}
        isRadioOnly={this.props.isRadioOnly}
        isUnavailable={this.props.isUnavailable}
        isExplicit={this.props.isExplicit}
        isClean={this.props.isClean}
        // RowItemSmallRowCollectedBadge
        isRowSmall={this.props.isRowSmall}
        isCollected={this.props.isCollected}
        // RowItemCustomBadge
        isCustomBadge={this.props.isCustomBadge}
        isBadgeFilled={this.props.isBadgeFilled}
        // RowItemRightColumnChild && RowItemRightColumnPlayButton
        isRowHovered={this.state.isRowHovered}
        onRightColChildClick={this.props.onRightColChildClick}
        isRightColPersistent={this.props.isRightColPersistent}
        rightColChild={this.props.rightColChild}
        rightColChildHover={this.props.rightColChildHover}
        qaRightColChild={this.props.qaRightColChild}
        rightColMainText={this.props.mainText}
        rightColOnFocus={() => this.setState({ isRowHovered: true })}
        rightColOnBlur={() => this.setState({ isRowHovered: false })}
        onlyShowRightColPlay={this.props.onlyShowRightColPlay}
        playButtonOnclick={this.props.playButtonOnclick}
        // RowItemEllipsis
        enableSourceCardMenu={this.props.enableSourceCardMenu}
        sourceCardMenuItems={this.props.sourceCardMenuItems}
        sourceCardToggleCallback={this.sourceCardToggleCallback}
        qaEllipsis={this.props.qaEllipsis}
        isClickableRow={this.props.isClickableRow}
        // RowItemShuffleBadge
        isShuffleDisabled={this.props.isShuffleDisabled}
        shuffleOnclickIfDisabled={this.props.shuffleOnclickIfDisabled}
        shuffleOnclick={this.props.shuffleOnclick}
      />
    );
  };

  renderDescription = () => {
    return (
      <RowItemDescription
        isRowHovered={this.state.isRowHovered}
        description={this.props.description}
        descriptionUrl={this.props.descriptionUrl}
        descriptionOnclick={this.props.descriptionOnclick}
        qaParagraphText={this.props.qaParagraphText}
      />
    );
  };

  renderRowItemContent = () => {
    const { rowOnclick, rowUrl } = this.props;
    const onRowClick = rowOnclick ? handlerNoPropagate(rowOnclick) : null;

    const classes = createBEM('RowItem__content', {
      opened: this.isInteractive(),
      hovered: this.state.isRowHovered && !this.props.noHover,
      fullRowLink: this.props.isFullRowLink,
    });

    const rowItem = (
      <div className={classes} onClick={onRowClick} role='button'>
        <div className='RowItem__columns'>
          {this.renderLeftCol()}
          {this.renderCenterCol()}
          {this.renderRightCol()}
        </div>

        {!!this.props.description && this.renderDescription()}
      </div>
    );

    if (onRowClick === null && rowUrl) {
      return (
        <Link className='RowItem__link' to={rowUrl} onClick={this.onRowClick}>
          {rowItem}
        </Link>
      );
    }

    return rowItem;
  };

  render() {
    const sourceCardMenuItems = this.props.sourceCardMenuItems;
    const hasSourceCard =
      !this.props.noHover && sourceCardMenuItems && this.props.enableSourceCardMenu;
    const classes = createBEM('RowItem', {
      borderBottom:
        this.props.hasBorderBottom && !this.props.hasBorderTopBottom && !this.props.noBorder,
      borderTopBottom: this.props.hasBorderTopBottom && !this.props.noBorder,
      dark: this.props.useDarkTheme,
      fadeHighlightAnimation: this.props.isFadeHighlight,
      hoverBottom: this.props.hasHoverBottom,
      hoverTop: this.props.hasHoverTop,
      large: !!this.props.description && !this.props.isRowSmall,
      light: this.props.useLightTheme,
      noBorder: this.props.noBorder,
      noHover: this.props.noHover,
      placeholder: this.props.isPlaceholder,
      small: this.props.isRowSmall,
      widthFull: this.props.isWidthFull,
      widthHalf: this.props.isWidthHalf,
    });

    if (this.props.isPlaceholder) {
      return this.renderPlaceholder(classes);
    }

    return (
      <ErrorBoundary>
        <div
          className={classes}
          onKeyDown={this.onKeyDown}
          onFocus={this.onFocus}
          onMouseEnter={this.handleMouseEnter}
          onMouseLeave={this.handleMouseLeave}
          data-qa={QATags.RI}
        >
          {hasSourceCard ? this.renderSourceCardWrappedContent() : this.renderRowItemContent()}
        </div>
      </ErrorBoundary>
    );
  }
}

export default RowItem;
